import { makeAutoObservable } from 'mobx'
import agent from '../agent'
import config from '../config'
import { RootStore } from './rootStore'
import { Country } from '../models/GeneralTypes'

/**
 * SECURITY NOTE: Token Storage
 * 
 * Currently, authentication tokens are stored in localStorage, which is vulnerable to XSS attacks.
 * 
 * SECURITY RISKS:
 * - localStorage is accessible to JavaScript, making tokens vulnerable to XSS attacks
 * - Tokens persist across browser sessions, increasing exposure window
 * - No automatic expiration or secure deletion mechanism
 * 
 * RECOMMENDED IMPROVEMENTS:
 * 1. Move token storage to httpOnly cookies (requires backend changes)
 * 2. Implement token refresh mechanism with short-lived access tokens
 * 3. Use sessionStorage instead of localStorage for non-persistent sessions
 * 4. Implement Content Security Policy (CSP) to mitigate XSS risks
 * 5. Add token encryption at rest in browser storage
 * 6. Implement automatic token cleanup on logout and session expiry
 * 
 * For now, we rely on:
 * - AWS Cognito's built-in token management
 * - XSS prevention through input sanitization
 * - Secure token transmission via HTTPS
 */
class AuthStore {
  rootStore: RootStore
  // SECURITY: Consider using sessionStorage for non-persistent sessions
  userId = window.localStorage.getItem('userId')
  // SECURITY: Token stored in localStorage - vulnerable to XSS. Consider httpOnly cookies.
  token = window.localStorage.getItem(`CognitoIdentityServiceProvider.${config.cognito.APP_CLIENT_ID}.${this.userId}.idToken`)

  get isAuthenticated() {
    return this.token != null
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  setToken(token: string | undefined) {
    this.token = token
  }

  setUserId(userId: string) {
    window.localStorage.setItem('userId', userId)
    this.userId = userId
  }

  currentUser() {
    return agent.Authentication.current()
  }

  async login(email: string, password: string) {
    return agent.Authentication.login(email, password).then(async congitoUser => {
      if (congitoUser.challengeName === 'NEW_PASSWORD_REQUIRED') await agent.Authentication.completeNewPassword(congitoUser, '')
      this.setToken(congitoUser.signInUserSession.idToken.jwtToken)
      this.setUserId(congitoUser.getUsername())
      await this.rootStore.userStore.getUser()
      return await this.currentUser()
    })
  }

  register(
    email: string,
    password: string,
    firstname: string,
    name: string,
    utm_source?: string,
    utm_medium?: string,
    utm_campaign?: string,
    utm_content?: string,
    referral_code?: string,
    country?: Country
  ) {
    return agent.Authentication.register(email, password, firstname, name, utm_source, utm_medium, utm_campaign, utm_content, referral_code, country)
  }

  forgotPassword(email: string) {
    return agent.Authentication.forgotPassword(email)
  }

  forgotPasswordSubmit(email: string, confirmationCode: string, newPassword: string) {
    return agent.Authentication.forgotPasswordSubmit(email, confirmationCode, newPassword)
  }

  async changePassword(oldPassword: string, newPassword: string) {
    const user = await this.currentUser()
    return agent.Authentication.changePassword(user, oldPassword, newPassword)
  }

  async logout() {
    console.log('logout')
    this.setToken(undefined)
    this.rootStore.brandStore.reset()
    this.rootStore.campaignStore.reset()
    // SECURITY: Clear all localStorage to remove any sensitive data
    // Note: This clears all localStorage, not just auth tokens
    // Consider clearing only auth-related keys for better user experience
    localStorage.clear()
    // SECURITY: Also clear sessionStorage to ensure complete cleanup
    sessionStorage.clear()
    return await agent.Authentication.logout()
  }

  setFeedback(onboardingFeedback: string) {
    return agent.Authentication.setFeedback({ onboardingFeedback })
  }

  checkReferralCode = agent.Users.checkReferralCode
}

export default AuthStore
